export type CommandFn<T extends readonly unknown[], U> = (...args: T) => U | undefined

/*
 * Augment every command with these functions to manipulate them.
 * Capabilities are a way for handlers to add new functionality and let experiences onboard them in a conditional way,
 * allowing for easier staggered rollout of new capabilities.
 */
export interface CommandMixin<T extends readonly unknown[], U> extends CommandFn<T, U> {
  isHandled(capabilities?: readonly string[]): boolean
  addHandler(handler: CommandFn<T, U>, capabilities?: readonly string[]): void
  removeHandler(): void
  readonly execute: CommandFn<T, U>
}

export type Command<T extends readonly unknown[] = readonly unknown[], U = unknown> = CommandMixin<T, U>

// eslint-disable-next-line functional/prefer-readonly-type
export type Commands = { [commandName: string]: Command }

const UNSET_COMMAND_FN: () => undefined = () => undefined

const equals = <T>(arr1: readonly T[], arr2: readonly T[]) =>
  arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index])

export const command = <T extends readonly unknown[], U>() => {
  let supportedCapabilities: readonly string[] = []
  let commandFn: CommandFn<T, U> = UNSET_COMMAND_FN
  let isCommandSet = false

  const addHandler: CommandMixin<T, U>['addHandler'] = (handler, capabilities = []) => {
    commandFn = (...args: T) => handler(...args)
    isCommandSet = !!handler
    supportedCapabilities = [...capabilities]
  }

  const removeHandler: CommandMixin<T, U>['removeHandler'] = () => {
    supportedCapabilities = []
    commandFn = UNSET_COMMAND_FN
    isCommandSet = false
  }

  const isHandled = (capabilities: readonly string[] = []) =>
    isCommandSet && (capabilities.length === 0 || equals(supportedCapabilities, capabilities))

  const execute = (...args: T) => commandFn(...args)

  return Object.assign(execute, {
    execute,
    isHandled,
    addHandler,
    removeHandler,
  })
}
